import type { RouteRecordRaw } from 'vue-router';
import PlaceholderView from '@/views/PlaceholderView.vue';
import SignInView from '@/views/SignInView.vue';
import RemoteLinkSignInView from '@/views/RemoteLinkSignInView.vue';
import LogoutView from '@/views/LogoutView.vue';
import RegisterView from '@/views/RegisterView.vue';
import ForgotPasswordView from '@/views/ForgotPasswordView.vue';
import ResetPasswordView from '@/views/ResetPasswordView.vue';
import ProfileView from '@/views/account/ProfileView.vue';
import AccountSettingsView from '@/views/account/AccountSettingsView.vue';
import OrganizationView from '@/views/account/OrganizationView.vue';
import UserStatsView from '@/views/UserStatsView.vue';
import UserInvitationsView from '@/views/UserInvitationsView.vue';
import DashboardView from '@/views/DashboardView.vue';
import LabsView from '@/views/emsl/LabsView.vue';
import LabOrdersView from '@/views/emsl/LabOrdersView.vue';
import OrdersView from '@/views/emsl/OrdersView.vue';
import AccreditationsView from '@/views/emsl/AccreditationsView.vue';
import LabAccreditationsView from '@/views/emsl/LabAccreditationsView.vue';
import CapacityView from '@/views/emsl/CapacityView.vue';
import SupportView from '@/views/emsl/SupportView.vue';
import AnalysisTypesView from '@/views/emsl/AnalysisTypesView.vue';
import AdvancedReportsView from '@/views/emsl/AdvancedReportsView.vue';
import SwitchOrganizationView from '@/views/emsl/SwitchOrganizationView.vue';
import ProjectsLandingView from '@/views/projects/ProjectsLandingView.vue';
import CreateProjectView from '@/views/projects/CreateProjectView.vue';
import ProjectFormsView from '@/views/projects/ProjectFormsView.vue';
import CreateFormView from '@/views/projects/CreateFormView.vue';
import EditFormView from '@/views/projects/EditFormView.vue';
import CloneFormView from '@/views/projects/CloneFormView.vue';
import FormDetailView from '@/views/projects/FormDetailView.vue';
import CreateSectionView from '@/views/projects/CreateSectionView.vue';
import EditSectionView from '@/views/projects/EditSectionView.vue';
import ProjectSettingsView from '@/views/projects/ProjectSettingsView.vue';
import ProjectDataView from '@/views/projects/ProjectDataView.vue';
import ProjectAnalysesView from '@/views/projects/ProjectAnalysesView.vue';
import CreateAnalysisView from '@/views/projects/CreateAnalysisView.vue';
import EditAnalysisView from '@/views/projects/EditAnalysisView.vue';
import NotFoundView from '@/views/errors/NotFoundView.vue';
import ServiceUnavailableView from '@/views/errors/ServiceUnavailableView.vue';

const Placeholder = PlaceholderView;

/**
 * Full route table mirroring legacy `formyulaCore.routes()` (defaults/init.js + custom/init.js).
 * Route `name` matches the legacy state name exactly so the auth guard's
 * nav-membership check works unchanged.
 *
 * Every route uses `PlaceholderView` until the corresponding feature is ported
 * (see the migration checklist in README.md).
 */
export const routes: RouteRecordRaw[] = [
  // ── Public ────────────────────────────────────────────────────────────────
  { name: 'main', path: '/', component: SignInView, meta: { public: true } },
  { name: 'register', path: '/register', component: RegisterView, meta: { public: true } },
  {
    name: 'acceptInvite',
    path: '/complete_invitation/:invite_token',
    component: RegisterView,
    meta: { public: true },
  },
  {
    name: 'forgotPassword',
    path: '/forgot_password',
    component: ForgotPasswordView,
    meta: { public: true },
  },
  {
    name: 'resetPassword',
    path: '/reset_password/:token',
    component: ResetPasswordView,
    meta: { public: true },
  },
  {
    name: 'remoteLinkLogIn',
    path: '/remote_link',
    component: RemoteLinkSignInView,
    meta: { public: true },
  },

  // ── Top-level authenticated ───────────────────────────────────────────────
  { name: 'logout', path: '/logout', component: LogoutView },
  { name: 'dashboard', path: '/dashboard', component: DashboardView },
  { name: 'account', path: '/account', component: ProfileView },
  { name: 'profile', path: '/account/profile', component: ProfileView },
  { name: 'account-settings', path: '/account/settings', component: AccountSettingsView },
  { name: 'organization', path: '/account/organization', component: OrganizationView },
  { name: 'explore', path: '/explore', component: Placeholder },
  { name: 'projectsSearch', path: '/projects/search/:category', component: Placeholder },
  { name: 'projectShowcase', path: '/project_showcase/:id', component: Placeholder },
  { name: 'invitation', path: '/invitations', component: UserInvitationsView },
  { name: 'userStats', path: '/userstats', component: UserStatsView },
  { name: 'reservations', path: '/orders', component: OrdersView },
  { name: 'switchOrganization', path: '/switchOrganization', component: SwitchOrganizationView },

  // ── User account flows (legacy `main.*` children) ─────────────────────────
  { name: 'main.password_reset', path: '/users/password_reset', component: Placeholder },
  { name: 'main.password_update', path: '/users/password_update', component: Placeholder },
  { name: 'main.register', path: '/users/register', component: Placeholder },
  { name: 'main.log_in', path: '/users/log_in', component: Placeholder },

  // ── EMSL ──────────────────────────────────────────────────────────────────
  { name: 'labOrders', path: '/labOrders', component: LabOrdersView },
  { name: 'accreditations', path: '/accreditations', component: AccreditationsView },
  { name: 'newAccreditation', path: '/accreditations/new', component: AccreditationsView },
  {
    name: 'editAccreditation',
    path: '/accreditations/:accreditationId/edit',
    component: AccreditationsView,
  },
  { name: 'analysisTypes', path: '/analysisTypes', component: AnalysisTypesView },
  { name: 'labs', path: '/labs', component: LabsView },
  { name: 'capacity', path: '/capacity', component: CapacityView },
  { name: 'labAccreditations', path: '/labAccreditations', component: LabAccreditationsView },
  { name: 'support', path: '/support', component: SupportView },
  { name: 'advancedReports', path: '/advanced-reports', component: AdvancedReportsView },
  {
    name: 'advancedReportsOrder',
    path: '/advanced-reports/:reservationCode',
    component: AdvancedReportsView,
  },

  // ── Projects ──────────────────────────────────────────────────────────────
  { name: 'projects', path: '/projects', component: ProjectsLandingView },
  { name: 'projectsAdd', path: '/projects/add', component: CreateProjectView },
  // Legacy `$urlRouterProvider.when('/projects/:id', '/projects/:id/forms')`.
  { path: '/projects/:id', redirect: (to) => `/projects/${String(to.params.id)}/forms` },
  { name: 'projectForms', path: '/projects/:id/forms', component: ProjectFormsView },
  { name: 'projectFormsAdd', path: '/projects/:id/forms/add', component: CreateFormView },
  { name: 'projectFormsDetail', path: '/projects/:id/forms/:form', component: FormDetailView },
  { name: 'projectData', path: '/projects/:id/data', component: ProjectDataView },
  { name: 'projectFormClone', path: '/projects/:id/forms/:form/clone', component: CloneFormView },
  { name: 'projectFormEdit', path: '/projects/:id/forms/:form_id/edit', component: EditFormView },
  { name: 'projectSettings', path: '/projects/:id/settings', component: ProjectSettingsView },
  {
    name: 'projectFormSectionsCreate',
    path: '/projects/:id/forms/:form_id/sections/create',
    component: CreateSectionView,
  },
  {
    name: 'projectSectionsEdit',
    path: '/projects/:id/forms/:form_id/sections/:section_id',
    component: EditSectionView,
  },
  { name: 'projectAnalyses', path: '/projects/:id/analyses', component: ProjectAnalysesView },
  { name: 'projectAnalysisAdd', path: '/projects/:id/analyses/add', component: CreateAnalysisView },
  {
    name: 'projectAnalysisEdit',
    path: '/projects/:id/analyses/:analysis_id/edit',
    component: EditAnalysisView,
  },

  // ── Accounts (legacy "accounts" namespace, distinct from "account") ──────
  { name: 'accounts', path: '/accounts', component: Placeholder },
  { name: 'accountAdd', path: '/accounts/add', component: Placeholder },
  { name: 'accountSettings', path: '/accounts/:id/settings', component: Placeholder },

  // ── Errors ───────────────────────────────────────────────────────────────
  {
    name: 'service-unavailable',
    path: '/503',
    component: ServiceUnavailableView,
    meta: { public: true },
  },
  { name: 'not-found', path: '/:pathMatch(.*)*', component: NotFoundView, meta: { public: true } },
];
